import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  DemoPersona,
  DemoPersonaRole,
  DemoSession,
  isDemoMode,
  resetDemoSession,
  resumeDemoSession,
  startDemoSession,
} from '../services/demoService';
import { setToken } from '../utils/jwtUtils';
import { getCurrentUser } from '../services/authService';
import { useAuth } from './AuthContext';
import { getDemoRouteForRole } from '../utils/roleNavigation';

/**
 * Holds the sales demo's sandbox and the four personas that come with it.
 *
 * Switching persona is deliberately not a login. Each persona arrives with its own JWT already
 * scoped to the visitor's sandbox, so switching is: store that token, replace the current user,
 * navigate. That reuses the same mid-session token swap the super-admin tenant switcher performs
 * (see services/tenantService.ts) rather than inventing a parallel auth path.
 *
 * Every persona swap and restart also throws away the React Query cache. Both change what
 * `/auth/me` and every subsequent request return without changing the query keys that cache them
 * (they're keyed on things like `['sessions']`, not on a persona or sandbox id), so a stale cache
 * would otherwise keep rendering the previous persona's — or the pre-reset sandbox's — data
 * until each query happened to go stale on its own (`staleTime` is 60s, see App.tsx). For a
 * persona switch that is a data leak shaped like a bug: the teacher view briefly rendering with
 * the student's cached submissions. For Restart it just makes the reset look like it did nothing.
 *
 * Inert unless REACT_APP_DEMO_MODE is set, so the provider can be mounted unconditionally.
 */

const SESSION_STORAGE_KEY = 'mwl_demo_sandbox_id';

interface DemoContextType {
  /** True only on the sales-demo build. */
  enabled: boolean;
  session: DemoSession | null;
  activeRole: DemoPersonaRole | null;
  loading: boolean;
  error: string | null;
  /**
   * Begin (or resume) a sandbox and enter it as the given persona.
   *
   * Resolves `true` once the persona is actually adopted, `false` if it failed — none of these
   * three action methods reject; a failure is reported through `error` state instead. Resolving
   * `false` rather than throwing means a caller that blindly does `await startDemo(role);
   * navigate(...)` would otherwise navigate on a failed start too. `error` alone can't stand in for
   * this: it's read from the closure captured at call time, so it's stale for the remainder of that
   * render regardless of the dispatch that follows the catch block. Callers MUST check the
   * resolved value and skip navigating (or whatever follow-up they had planned) when it is `false`.
   */
  startDemo: (role?: DemoPersonaRole) => Promise<boolean>;
  /** Same success contract as `startDemo`: resolves `false`, never rejects, on failure. */
  switchPersona: (role: DemoPersonaRole) => Promise<boolean>;
  /**
   * Discard everything the visitor changed and start the sandbox over.
   *
   * Same success contract as `startDemo`.
   */
  restartDemo: () => Promise<boolean>;
  expiresAt: Date | null;
}

const DemoContext = createContext<DemoContextType | undefined>(undefined);

const readStoredSandboxId = (): number | null => {
  const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
  const parsed = raw === null ? NaN : Number(raw);

  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export function DemoProvider({ children }: { children: ReactNode }) {
  const enabled = isDemoMode();
  const { updateCurrentUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [session, setSession] = useState<DemoSession | null>(null);
  const [activeRole, setActiveRole] = useState<DemoPersonaRole | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Guards against two adoptions racing each other. `adopt` is not re-entrancy-safe on its own —
   * it awaits a network round trip (`getCurrentUser`) and another (`invalidateQueries`) before
   * committing anything — and the guided tour calls it in quick succession as it walks through
   * personas. If an earlier call's response happens to land after a later one's, it would overwrite
   * the correct, current persona with a stale one. Each call claims the latest sequence number
   * before awaiting anything and checks it still owns that number before committing state; a call
   * that's been superseded quietly stops instead of clobbering the newer result.
   */
  const adoptSeq = useRef(0);

  /**
   * Adopt a persona: install its token, load the user through the normal /auth/me path, then
   * throw away every cached query.
   *
   * The user is fetched rather than assembled from the persona payload so that the object in
   * AuthContext is exactly what the rest of the app expects — including the profile — instead of a
   * near-miss shape that works until some page reads a field the demo forgot to supply.
   *
   * The cache invalidation runs on every adoption — initial entry, a manual persona switch, and a
   * restart alike — because all three change what the same query keys (`['sessions']`,
   * `['leaderboard']`, ...) resolve to without changing the keys themselves. Skipping it after a
   * switch would render one persona's page with another's cached data; skipping it after a restart
   * makes the reset look like it silently did nothing.
   *
   * It is deliberately NOT awaited. `adopt` resolves as soon as the new role is committed, and the
   * caller (`switchPersona`/`restartDemo`) navigates immediately after that — every caller does
   * this. If invalidation instead blocked the resolve, the role would already be committed while
   * the route was still the *old* persona's, for as long as the refetch burst took: long enough for
   * ProtectedRoute to render that mismatched pair, decide the new role isn't allowed on the old
   * route, and redirect to `/unauthorized` — which does not exist as a route, so `Navigate`'s own
   * catch-all sends it on to `/404` — a moment before the real navigation lands. Firing it instead
   * of awaiting it keeps the role-commit and the navigate() call adjacent, closing that window; the
   * invalidated queries simply refetch in the background and settle on whichever page the visitor
   * ends up on.
   */
  const adopt = useCallback(
    async (persona: DemoPersona) => {
      const requestId = ++adoptSeq.current;
      setToken(persona.token);

      const user = await getCurrentUser();

      // A newer adoption started while this one was in flight — let it win.
      if (requestId !== adoptSeq.current) {
        return;
      }

      updateCurrentUser(user);
      setActiveRole(persona.role);
      queryClient.invalidateQueries();
    },
    [updateCurrentUser, queryClient]
  );

  const enter = useCallback(
    async (next: DemoSession, role?: DemoPersonaRole) => {
      const persona =
        next.personas.find(candidate => candidate.role === role) ?? next.personas[0];

      if (!persona) {
        throw new Error('This demo sandbox has no personas available.');
      }

      setSession(next);
      window.localStorage.setItem(SESSION_STORAGE_KEY, String(next.sandboxId));
      await adopt(persona);
    },
    [adopt]
  );

  const startDemo = useCallback(
    async (role?: DemoPersonaRole): Promise<boolean> => {
      if (!enabled) {
        return false;
      }

      setLoading(true);
      setError(null);

      try {
        const stored = readStoredSandboxId();
        // Reuse the sandbox from a previous page load when it is still alive, so a refresh does
        // not silently throw away everything the visitor did.
        const existing = stored === null ? null : await resumeDemoSession(stored);

        await enter(existing ?? (await startDemoSession()), role);
        return true;
      } catch (caught) {
        console.error('[Demo] Could not start a demo session:', caught);
        setError('The demo could not be started. Please try again in a moment.');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [enabled, enter]
  );

  const switchPersona = useCallback(
    async (role: DemoPersonaRole): Promise<boolean> => {
      if (!session) {
        return startDemo(role);
      }

      const persona = session.personas.find(candidate => candidate.role === role);

      if (!persona) {
        setError(`This demo does not include a ${role} account.`);
        return false;
      }

      setLoading(true);
      setError(null);

      try {
        await adopt(persona);
        return true;
      } catch (caught) {
        console.error(`[Demo] Could not switch to the ${role} view:`, caught);
        setError(`Could not switch to the ${role} view.`);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [session, startDemo, adopt]
  );

  const restartDemo = useCallback(async (): Promise<boolean> => {
    if (!session) {
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      await enter(await resetDemoSession(session.sandboxId), activeRole ?? undefined);

      // The visitor may be sitting on a page the reset just invalidated the point of (an
      // enrollment they finished, a session they were mid-edit on). Landing back on the role's
      // home page makes the reset unambiguous instead of leaving them on a stale-looking screen.
      if (activeRole) {
        navigate(getDemoRouteForRole(activeRole));
      }

      return true;
    } catch (caught) {
      console.error('[Demo] Could not restart the demo:', caught);
      setError('The demo could not be restarted. Please reload the page.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [session, activeRole, enter, navigate]);

  // Resume silently on load so a refresh keeps the visitor where they were, without forcing them
  // back through the landing page.
  useEffect(() => {
    if (!enabled || session) {
      return;
    }

    const stored = readStoredSandboxId();

    if (stored === null) {
      return;
    }

    let cancelled = false;

    resumeDemoSession(stored)
      .then(resumed => {
        if (resumed && !cancelled) {
          setSession(resumed);
        } else if (!resumed) {
          window.localStorage.removeItem(SESSION_STORAGE_KEY);
        }
      })
      .catch(caught => console.error('[Demo] Could not resume the demo session:', caught));

    return () => {
      cancelled = true;
    };
  }, [enabled, session]);

  const value = useMemo<DemoContextType>(
    () => ({
      enabled,
      session,
      activeRole,
      loading,
      error,
      startDemo,
      switchPersona,
      restartDemo,
      expiresAt: session?.expiresAt ? new Date(session.expiresAt) : null,
    }),
    [enabled, session, activeRole, loading, error, startDemo, switchPersona, restartDemo]
  );

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

export const useDemo = (): DemoContextType => {
  const context = useContext(DemoContext);

  if (context === undefined) {
    throw new Error('useDemo must be used within a DemoProvider');
  }

  return context;
};
